import React from 'react';
import { Package, ArrowRight } from 'lucide-react';
import { apiService } from '../services/api';
import useApi from '../hooks/useApi';
import LoadingSpinner, { SkeletonCard } from './LoadingSpinner';
import { ErrorMessage } from './ErrorBoundary';

const Products = () => {
  const { data: products, loading, error, refetch } = useApi(() => apiService.getProducts(), []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (error) {
    return (
      <section id="products" className="section-padding bg-white">
        <div className="container mx-auto px-6 lg:px-8">
          <ErrorMessage message={error} onRetry={refetch} />
        </div>
      </section>
    );
  }

  return (
    <section id="products" className="section-padding bg-white">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="h-1 w-12 bg-[#2aa996]"></div>
            <span className="text-[#2aa996] font-semibold uppercase tracking-wider text-sm">
              Our Products
            </span>
            <div className="h-1 w-12 bg-[#2aa996]"></div>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Industry-Specific <span className="gradient-text">Software Solutions</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Built on years of industry expertise, our software products solve real business challenges 
            for freight forwarding, invoicing, travel management, and modern e-commerce operations.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {loading ? (
            // Loading skeletons
            [...Array(4)].map((_, index) => (
              <SkeletonCard key={index} />
            ))
          ) : (
            products?.map((product, index) => (
              <div
                key={product.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group border border-gray-100"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#2aa996] text-white text-xs px-3 py-1 rounded-full font-medium">
                      {product.category}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <Package className="text-[#2aa996]" size={24} />
                    </div>
                  </div>
                </div>

                <div className="p-6 md:p-8">
                  <h3 className="text-xl font-bold mb-3 text-gray-800">
                    {product.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {product.description}
                  </p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3">Key Features:</h4>
                    <div className="flex flex-wrap gap-2">
                      {product.features?.map((feature, idx) => (
                        <span
                          key={idx}
                          className="bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    onClick={scrollToContact}
                    className="flex items-center space-x-2 text-[#2aa996] hover:text-[#1e7a6b] transition-colors duration-300 font-medium group-hover:translate-x-1"
                  >
                    <span>Learn More</span>
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="text-center mt-16">
          <p className="text-lg text-gray-600 mb-8">
            Need a custom solution for your industry?
          </p>
          <button 
            onClick={scrollToContact}
            className="btn-primary text-lg px-8 py-4"
          >
            Discuss Your Requirements
          </button>
        </div>
      </div>
    </section>
  );
};

export default Products;


